// ============================================================
// TILT — BOSS KIT  (src/BossKit.js)
// ------------------------------------------------------------
// THE MONSTER FRAMEWORK. One registry, every table, every mode.
//
// ── THE ONE IDEA ─────────────────────────────────────────────
//   A BOSS IS A BEING. It has ANIMA (its life — persists across
//   your drains, the thing you dim to zero) and LYF (its guard —
//   restores IN FULL on every drain: THE LYF CYCLE, R27/28).
//   Chip damage is worthless. You break the guard THIS BALL, or
//   the wall heals and you start over.
//
//   Fell the being and you have beaten the table's keeper. In a
//   duel, the being on YOUR table IS YOUR OPPONENT — fell it and
//   you have beaten THEM.
//
// ── WHY THE MOVES LIVE HERE AND NOT IN A TABLE FILE ──────────
// caP'n's law for bosses: "every boss can have kind of the same
// effect but reskinned." That sentence IS an architecture. The
// EFFECTS are universal (stop the flippers, hasten the ball,
// heave the gravity) — they touch the engine, which every table
// shares. The NAMES are the table's costume. So:
//
//   BossKit  owns  WHAT a move does        (one copy, ever)
//   the table owns WHAT the move is CALLED (`boss.moves[].name`)
//
// The Ringmaster's THE HUSH and some future Leviathan's THE
// UNDERTOW are the same `fracture` — one apply(), zero drift,
// and a new boss costs a stat block and four names. This is the
// PackArt.js law wearing a mask.
//
// ── THE ONE GATE (R-UNIVERSAL) ───────────────────────────────
// applyBossMove() is the ONLY door a monster attack enters
// through. The solo AI calls it. The SabotageBus calls it when a
// human opponent drives the monster in PvP. The NPC would call
// it. No caller decides legality inline — same law as Enforcer,
// same reason: a rule that exists in two places is two rules,
// and one of them is wrong.
//
// ── EMPIRE VOCABULARY (the Dial law) ─────────────────────────
// Internal ids are Empire words even where the marquee is not:
// `fracture` (never "stun"), `hasten` (never "speedup"), Anima
// (never "hp"), Lyf (never "shield"). The skin renames the
// SCREEN. It never renames the CODE.
//
// ── DETERMINISM ──────────────────────────────────────────────
// The AI draws from its OWN seeded generator (boss._rng), never
// from world.rng. The AI's beat runs on frame time (like every
// table's onTick), so drawing from the physics stream here would
// perturb ball trajectories differently per frame rate. A boss
// may be cruel. It may not be a source of nondeterminism.
// ============================================================

import { MagnetField } from "./physics/World";

// ══════════════════════════════════════════════════════════════
// THE MOVE REGISTRY — what a monster can DO to a table.
// ------------------------------------------------------------
// Shape: { id, name, glyph, blurb, cost, khaoCost, duration,
//          weight, apply(engine) }
//
//   cost     — the CHARGE a human opponent pays to drive this
//              move in a duel, and what the solo AI's own meter
//              pays. (⚠ legacy scale, 0–100 — condenses to
//              khaoCost when the Aenergy engine lands, same as
//              Powers.js.)
//   khaoCost — the real Unit-2 price. Nothing reads it yet.
//   weight   — the solo AI's pick bias. A boss should reach for
//              its signature more often than its filler.
//   apply    — the effect, resolved against the ENGINE. Returns
//              an fx descriptor or null. NEVER throws upward —
//              the gate catches. Same posture as Powers.apply.
//
// ⚠ EVERY MOVE MUST BE SURVIVABLE AND READABLE. The Forgemaster
//   header's law holds here: an unexplained impulse is the most
//   infuriating thing a table can do. Every move announces
//   itself via the gate's log line, and every duration is short
//   enough that the player keeps the ball... if they're good.
// ══════════════════════════════════════════════════════════════
export const BOSS_MOVES = {

  // ── FRACTURE — the flippers refuse you ─────────────────────
  // caP'n's founding example: "he stops our flippers for 1
  // second." In Empire terms that is a FRACTURE — the guard (your
  // flippers ARE your guard) drops. 1.0s is derived, not felt: a
  // ball crossing the flipper band takes ~0.5–0.8s to arrive from
  // mid-table, so one second is exactly long enough to make a
  // cradled ball a coin-flip and never long enough to be a
  // guaranteed drain from a good position. A move that ALWAYS
  // costs a ball isn't an attack, it's a tax, and taxes aren't
  // scary — they're boring.
  fracture: {
    id: "fracture", name: "FRACTURE", glyph: "⛓",
    cost: 30, khaoCost: 3, duration: 1.0, weight: 3, tint: "hot",
    blurb: "The flippers refuse you. One second. It's enough.",
    apply: (engine) => {
      engine.fractureFlippers(BOSS_MOVES.fracture.duration);
      return { fx: "mode", label: "FRACTURE" };
    },
  },

  // ── HASTEN — the ball forgets to be careful ────────────────
  // caP'n's second example: "he speeds up the ball." Hostile
  // Overclock — same speedMul field the player's own Impera uses,
  // smaller and shorter because it's being done TO you: the
  // player's 1.85× is a choice they made; 1.5× uninvited is
  // plenty. (Overclock's own law applies: speedMul, never a
  // velocity write — a velocity write is a teleport with makeup.)
  hasten: {
    id: "hasten", name: "HASTEN", glyph: "⤁",
    cost: 25, khaoCost: 2, duration: 3.0, weight: 2, tint: "ember",
    blurb: "Your ball, one and a half times as honest.",
    apply: (engine) => {
      const balls = engine.liveBalls();
      if (!balls.length) return null;
      for (const b of balls) b.speedMul = Math.max(b.speedMul, 1.5);
      engine.addTimer(BOSS_MOVES.hasten.duration, () => {
        for (const b of engine.liveBalls()) if (b.speedMul > 1.4) b.speedMul = 1;
      });
      return { fx: "overclock_hostile" };
    },
  },

  // ── DRAG — the whole table leans toward the drain ──────────
  // A gravity heave on the BALL, not the world — gravityMul is a
  // per-ball field the solver already reads (the plunger uses it
  // to hold the lane). Short and heavy: the ball gets hungry for
  // the floor and every shot lands shallower than aimed.
  drag: {
    id: "drag", name: "DRAG", glyph: "▼",
    cost: 25, khaoCost: 2, duration: 2.6, weight: 2, tint: "violet",
    blurb: "The floor gets hungrier.",
    apply: (engine) => {
      const balls = engine.liveBalls();
      if (!balls.length) return null;
      for (const b of balls) b.gravityMul = 1.65;
      engine.addTimer(BOSS_MOVES.drag.duration, () => {
        for (const b of engine.liveBalls()) if (b.gravityMul > 1.5) b.gravityMul = 1;
      });
      return { fx: "mode", label: "DRAG" };
    },
  },

  // ── LURE — a magnet over the mouth of the drain ────────────
  // The monster's version of the Magnetize sabotage, pre-aimed at
  // the one point that matters. It reuses MagnetField — the same
  // deterministic, ttl'd field the player's power uses — so the
  // renderer already knows how to draw it and the solver already
  // knows how to expire it. Placed just ABOVE the flipper line:
  // fightable with a flip, fatal to a ball left drifting.
  lure: {
    id: "lure", name: "LURE", glyph: "◎",
    cost: 28, khaoCost: 2, duration: 1.8, weight: 2, tint: "hot",
    blurb: "Something under the floor wants your ball.",
    apply: (engine) => {
      const W = engine.table.W || 400;
      const flipY = engine.flippers[0]?.pivot?.y ?? ((engine.table.H || 700) - 110);
      const at = { x: W / 2, y: flipY + 26 };
      engine.world.addField(MagnetField(at.x, at.y, {
        radius: 120, strength: 820, ttl: BOSS_MOVES.lure.duration,
      }));
      return { fx: "magnet_hostile", at, r: 120, ttl: BOSS_MOVES.lure.duration };
    },
  },

  // ── JOLT — a shove with no hands ────────────────────────────
  // The cheap filler: a hostile nudge from nowhere. Small impulse,
  // seeded direction (the boss's OWN rng — see the determinism
  // note in the header). It exists so the AI has something to do
  // with a half-full meter; it should annoy, never end.
  jolt: {
    id: "jolt", name: "JOLT", glyph: "↯",
    cost: 15, khaoCost: 1, duration: 0, weight: 1, tint: "bone",
    blurb: "A shove with no hands on it.",
    apply: (engine) => {
      const balls = engine.liveBalls();
      if (!balls.length) return null;
      const r = engine.boss?._rng || Math.random;   // fallback never taken in play
      const a = r() * Math.PI * 2;
      const IMPULSE = 95;
      for (const b of balls) {
        if (b.frozen > 0) continue;
        b.vel.x += Math.cos(a) * IMPULSE;
        b.vel.y += Math.sin(a) * IMPULSE;
      }
      return { fx: "nudge", dir: { x: Math.cos(a), y: Math.sin(a) } };
    },
  },
};

// ══════════════════════════════════════════════════════════════
// THE GUARDIAN VERBS — what a player does FOR their monster.
// ------------------------------------------------------------
// In a duel, the being on your opponent's table is YOUR avatar.
// They are trying to fell it. These are the two ways you spend
// your own meter to keep it standing — and they travel the SAME
// wire, in the SAME direction, as a sabotage (toward the enemy
// table), because that is where your monster LIVES.
//
//   BULWARK — the guard re-forms. Lyf to full, Fracture ENDS.
//             This is the counter to their burst window, and it
//             is deliberately the most expensive thing on the
//             rail: slamming the door mid-combo should hurt the
//             one slamming it too.
//   MEND    — Anima back, capped at the printed max. Slower,
//             cheaper, the long game.
//
// The tension caP'n asked for — "choosing which to spend your
// khao on will prove vital" — is exactly this rail versus the
// sabotage rail versus your own powers. One meter. Four hungers.
// ══════════════════════════════════════════════════════════════
export const GUARDIAN_MOVES = {
  bulwark: {
    id: "bulwark", name: "BULWARK", glyph: "⛨",
    cost: 32, khaoCost: 3, heal: 0, tint: "ice",
    blurb: "The guard re-forms. The window slams.",
    apply: (engine, B) => {
      B.lyf = B.lyfMax;
      B.fractured = false;
      return { fx: "mode", label: "BULWARK" };
    },
  },
  mend: {
    id: "mend", name: "MEND", glyph: "✚",
    cost: 26, khaoCost: 2, heal: 6, tint: "good",
    blurb: "Six Anima back. The show goes on.",
    apply: (engine, B) => {
      B.anima = Math.min(B.animaMax, B.anima + GUARDIAN_MOVES.mend.heal);
      return { fx: "mode", label: "MEND" };
    },
  },
};

export const getBossMove     = (id) => BOSS_MOVES[id] || null;
export const getGuardianMove = (id) => GUARDIAN_MOVES[id] || null;
export const isMonsterVerb   = (id) => !!(BOSS_MOVES[id] || GUARDIAN_MOVES[id]);

// ══════════════════════════════════════════════════════════════
// THE ONE GATE — every monster action enters here.
// ------------------------------------------------------------
// Callers: bossAiTick (solo), TiltEngine.receiveSabotage (duel,
// via the SabotageBus). NOBODY else applies a move.
//
// A move that lands between balls is QUEUED, not wasted — the
// same law receiveSabotage already lives by: sabotage should
// always land eventually or the sender feels cheated by netcode.
// ══════════════════════════════════════════════════════════════
export const applyBossMove = (engine, moveId, from = "?") => {
  const m = BOSS_MOVES[moveId];
  const B = engine?.boss;
  if (!m || !B || B.felled) return null;

  if (engine.phase !== "live") {
    engine.incoming.push({ powerId: moveId, arg: null, from });
    return null;
  }

  let fx = null;
  try { fx = m.apply(engine); } catch (e) {
    console.warn(`[BossKit] move "${moveId}" threw:`, e?.message);
    return null;
  }
  if (!fx) return null;

  // The skin: the table named this move; the log speaks its name.
  const label = B.skin?.[moveId] || m.name;
  engine._fx({ ...fx, hostile: true });
  engine._push("sabotage", `👹 ${B.name}: ${label}!`);
  engine.onEvent({ type: "boss_move", move: moveId, from });
  return fx;
};

/** A guardian verb has no phase gate — a monster exists between
 *  balls, and tending it there is legal. Felled is final: the
 *  duel is decided, and no MEND un-decides it. */
export const applyGuardianMove = (engine, moveId, from = "?") => {
  const g = GUARDIAN_MOVES[moveId];
  const B = engine?.boss;
  if (!g || !B || B.felled) return null;

  let fx = null;
  try { fx = g.apply(engine, B); } catch (e) {
    console.warn(`[BossKit] guardian "${moveId}" threw:`, e?.message);
    return null;
  }
  if (!fx) return null;

  const label = g.name;
  engine._fx({ ...fx, hostile: true });
  engine._push("sabotage", `✚ ${B.name} is tended — ${label}`);
  engine.onEvent({ type: "boss_guarded", move: moveId, from });
  return fx;
};

// ══════════════════════════════════════════════════════════════
// THE SOLO AI — the monster drives itself when no human does.
// ------------------------------------------------------------
// Economy: the boss has its own Khao meter (0–100, same scale as
// the player's charge — one vocabulary, two combatants). It
// fills at a rate set by the table's MENACE — a 0..3 tier the
// table derives from its own state (the Cirque maps it straight
// onto the heat phase). Tier 0 fills NOTHING and fires NOTHING:
//
//   THE TABLE MAY NEVER ATTACK SOMEONE WHO HASN'T EARNED IT.
//
// That is the Foundry's oldest law (DORMANT means dormant), and
// the boss inherits it whole. 288,000 verified frames say the
// dormant table has never once swung first; this file must never
// be the thing that breaks that record.
//
// The beat: every AI_BEAT seconds the boss considers acting. It
// only sometimes does (a boss that swings on every legal frame
// is a metronome, and a metronome is furniture). Picks are
// weighted, affordable-only, from the boss's own seeded rng.
// ══════════════════════════════════════════════════════════════
const MENACE_RATE = [0, 3.2, 5.5, 8.5];   // Khao/s per menace tier
const AI_BEAT     = 2.5;                  // seconds between considerations
const AI_APPETITE = 0.62;                 // chance it acts on a legal beat

export const bossAiTick = (engine, dt) => {
  const B = engine?.boss;
  if (!B || B.felled) return;
  if (engine.phase !== "live") return;

  const tier = Math.max(0, Math.min(3,
    engine.table.boss?.menace?.(engine) ?? 1));
  if (tier <= 0) return;                  // dormant. always. see above.

  B.khao = Math.min(B.khaoMax, B.khao + MENACE_RATE[tier] * dt);

  B._beat += dt;
  if (B._beat < AI_BEAT) return;
  B._beat -= AI_BEAT;

  if (B._rng() > AI_APPETITE) return;     // it doesn't always swing

  const kit = B.moves
    .map((id) => BOSS_MOVES[id])
    .filter((m) => m && B.khao >= m.cost);
  if (!kit.length) return;

  // Weighted pick from the boss's own generator.
  const total = kit.reduce((n, m) => n + m.weight, 0);
  let roll = B._rng() * total;
  let pick = kit[kit.length - 1];
  for (const m of kit) { roll -= m.weight; if (roll <= 0) { pick = m; break; } }

  B.khao -= pick.cost;
  applyBossMove(engine, pick.id, B.name);
};

// ══════════════════════════════════════════════════════════════
// BOOT INTEGRITY — same posture as tables/index.js: a crash you
// can read is a gift. A boss with a move id the registry doesn't
// know would otherwise be a monster that silently can't fight.
// ══════════════════════════════════════════════════════════════
export const validateBossDef = (def) => {
  const bad = [];
  if (!def?.name) bad.push("boss has no name");
  if (!(def?.anima > 0)) bad.push("boss.anima must be > 0 — a being has a life");
  if (!(def?.lyf >= 0))  bad.push("boss.lyf must be >= 0 (0 = permanently Fractured, R29)");
  for (const m of def?.moves || []) {
    if (!BOSS_MOVES[m.id]) bad.push(`unknown boss move "${m.id}"`);
  }
  for (const id of def?.duelKit || []) {
    if (!BOSS_MOVES[id]) bad.push(`duelKit names unknown move "${id}"`);
  }
  return { valid: bad.length === 0, violations: bad };
};

export default {
  BOSS_MOVES, GUARDIAN_MOVES,
  getBossMove, getGuardianMove, isMonsterVerb,
  applyBossMove, applyGuardianMove, bossAiTick, validateBossDef,
};
